import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  HardHat, FileText, Briefcase, BookOpen, Users, Star, 
  MapPin, Shield, CheckCircle, AlertTriangle, Clock, Wrench, 
  Package, Building2, Box, Hammer, LayoutGrid,
  type LucideIcon 
} from 'lucide-react';
import { useConsolidatedPermissions, type ConsolidatedPermissions } from '@/hooks/useConsolidatedPermissions';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useHomeCards, type HomeCard } from '@/hooks/useHomeCards';
import { useAuth } from '@/hooks/useAuth';
import { useUserPermissionCodes } from '@/hooks/useUserRoles';
import { cn } from '@/lib/utils';

// Icon whitelist — map string names to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  HardHat, FileText, Briefcase, BookOpen, Users, Star,
  MapPin, Shield, CheckCircle, AlertTriangle, Clock, Wrench,
  Package, Building2, Box, Hammer, LayoutGrid,
};

const resolveIcon = (iconName: string): LucideIcon => {
  return ICON_MAP[iconName] || MapPin;
};

const hasPermission = (
  permissions: ConsolidatedPermissions | undefined, 
  isAdmin: boolean, 
  card: HomeCard,
  codes?: string[]
): boolean => {
  if (isAdmin) return true;
  if (card.is_coming_soon) return true;
  if (!card.permission_key || !permissions) return false;
  
  if (permissions[card.permission_key as keyof ConsolidatedPermissions]) return true;
  // alt_permissions: đọc CẢ consolidated (key cũ) lẫn mã quyền động (key mới không có trong consolidated)
  if (card.alt_permissions?.some(k => permissions[k as keyof ConsolidatedPermissions] || codes?.includes(k))) return true;
  
  return false;
};

export default function HomeCardGrid() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: permissions, isLoading: permLoading } = useConsolidatedPermissions();
  const { isAdmin, isLoading: adminLoading } = useAdminCheck();
  const codes = useUserPermissionCodes();
  const { data: cards, isLoading: cardsLoading } = useHomeCards();

  if (!user) return null;

  if (permLoading || adminLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i} className="h-32 animate-pulse bg-muted" />
        ))}
      </div>
    );
  }

  const activeCards = (cards || []).filter(c => c.is_active);
  const visibleCards = activeCards.filter(card => hasPermission(permissions, isAdmin, card, codes.data));

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {visibleCards.map(card => {
        const Icon = resolveIcon(card.icon_name);
        const disabled = card.is_coming_soon;

        return (
          <Card
            key={card.key}
            className={cn(
              'relative transition-all duration-200 group overflow-hidden',
              disabled 
                ? 'opacity-50 cursor-not-allowed' 
                : 'cursor-pointer hover:shadow-md hover:border-primary/30 hover:-translate-y-0.5'
            )}
            onClick={() => {
              if (!disabled && card.route) navigate(card.route);
            }}
          >
            <CardContent className="flex flex-col items-center justify-center text-center gap-3 p-6 h-48">
              <div className={cn(
                "h-24 w-24 rounded-lg flex items-center justify-center overflow-hidden",
                !card.image_url && "bg-primary/10"
              )}>
                {card.image_url ? (
                  <img 
                    src={card.image_url} 
                    alt={card.label}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Icon className={cn(
                    'h-12 w-12 transition-colors',
                    disabled ? 'text-muted-foreground' : 'text-primary group-hover:text-primary/80'
                  )} />
                )}
              </div>
              <div>
                <p className="font-medium text-sm">{card.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{card.description}</p>
              </div>
              {disabled && (
                <Badge variant="secondary" className="absolute top-2 right-2 text-[10px] px-1.5 py-0">
                  Sắp ra mắt
                </Badge>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
